/**
 * ReservoirHubPage.tsx — Single hierarchical hub for the entire reservoir
 * design workflow.
 *
 * Large-button workflow tiles grouped by phase (site → diffuser → array →
 * reservoir → assessment → engineering → compliance → pilot → reports).
 * Every page in the workflow appears here with:
 *   · A large icon and clear label
 *   · A short description of what that page does
 *   · A green "saved" dot if persisted session data exists for that page
 *
 * Top-right "Session" button opens a popup listing every saved key, when it
 * was last updated, and a compact preview of its contents.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Waves, Database, X,
  CheckCircle2, FileDown, RefreshCcw,
  MapPin, ClipboardList, Upload,
  Layers, Sparkles, Grid3X3,
  Wind, Mountain, FlaskConical, Workflow,
  Thermometer, Leaf, BarChart3, ClipboardCheck,
  ShieldAlert, ShieldCheck, Volume2, Wrench, TrendingUp,
  BookOpen, FileText, TestTube, Activity, Boxes,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import FeatureCard from '@/components/FeatureCard';
import {
  listAllPersistedKeys, getPersistedTs, exportAllPersisted,
} from '@/lib/sessionPersistence';
import { toast } from 'sonner';
import AwwaM37Toggle from '@/components/AwwaM37Toggle';
import { useModulesEnabled, MODULES } from '@/lib/moduleRegistry';

type LucideIcon = typeof Waves;

interface Step {
  label: string;
  path: string;
  icon: LucideIcon;
  desc: string;
  savedKey?: string;     // localStorage key that, if present, lights the green dot
}

interface Phase {
  number: number;
  title: string;
  subtitle: string;
  colour: string;
  /** Accent colour passed to the FeatureCard tile for this phase. */
  accent: string;
  steps: Step[];
}

const PHASES: Phase[] = [
  {
    number: 1,
    title: 'Site & Conditions',
    subtitle: 'Capture the reservoir, the catchment and the field data',
    colour: 'from-sky-700/40 to-sky-900/20 border-sky-700',
    accent: '#38bdf8',
    steps: [
      { label: 'Reservoir Management',  path: '/reservoir-management',  icon: Waves,         desc: 'Reservoir physical characteristics, catchment, residence time', savedKey: 'rb_reservoir_design_v1' },
      { label: 'Site Visit Form',       path: '/site-data-capture',     icon: ClipboardList, desc: 'Pre/post-award field record across bathymetry, sediment, water chemistry, power, acoustics', savedKey: 'rb_site_data_capture_v1' },
      { label: 'Simulation Data Hub',   path: '/simulation-data',       icon: Database,      desc: 'Every pre-selection datum — toggle "apply" to override defaults everywhere in the tool', savedKey: 'rb_simulation_data_v1' },
      { label: 'Bathymetry Import',     path: '/bathymetry-import',     icon: Upload,        desc: 'Upload survey CSV; build the contour-aware bed model', savedKey: 'rb_bathymetry_v1' },
    ],
  },
  {
    number: 2,
    title: 'Diffuser Design',
    subtitle: 'Single-diffuser physics: bubble, SOTE, plume, fouling, comparison',
    colour: 'from-cyan-700/40 to-cyan-900/20 border-cyan-700',
    accent: '#22d3ee',
    steps: [
      { label: 'Reservoir Aeration Spec', path: '/reservoir-aeration-spec', icon: Wind,    desc: 'Aeration system design specification', savedKey: 'rb_reservoir_aeration_v1' },
      { label: 'Diffuser Module',       path: '/diffuser-module',       icon: Layers,        desc: 'Single-module diffuser physics: pores, SOTE, bubble, AOTR', savedKey: 'rb_diffuser_module_v1' },
      { label: 'Custom Diffuser',       path: '/custom-diffuser',       icon: Sparkles,      desc: 'Build a custom diffuser geometry and propagate it through the tool', savedKey: 'rb_custom_diffuser_v1' },
      { label: 'Diffuser Comparison',   path: '/diffuser-comparison',   icon: Grid3X3,       desc: 'Side-by-side scoring of 6 candidate diffusers' },
      { label: 'Pore Release Geometry', path: '/pore-release-geometry', icon: Wind,          desc: 'Pore-axis angle, initial velocity vector, plume centre-line under wind' },
      { label: 'Fouling Drift (10y)',   path: '/fouling-drift',         icon: TrendingUp,    desc: '10-year membrane creep and SOTE drift with cleaning cycles', savedKey: 'rb_fouling_drift_v1' },
    ],
  },
  {
    number: 3,
    title: 'Module Array & Layout',
    subtitle: 'Aggregate single modules into the full bed-level array',
    colour: 'from-teal-700/40 to-teal-900/20 border-teal-700',
    accent: '#2dd4bf',
    steps: [
      { label: 'Module Array',          path: '/module-array',          icon: Grid3X3,       desc: 'Arrangement of multiple modules across reservoir zones', savedKey: 'rb_module_array_v1' },
      { label: 'Reservoir Layout',      path: '/reservoir-layout',      icon: MapPin,        desc: 'Full reservoir layout plan with array zones and exclusion zones' },
      { label: 'Plume Coverage Map',    path: '/plume-coverage',        icon: MapPin,        desc: 'Designed array overlaid on the reservoir; AWWA M37 compliance check' },
      { label: 'Sampling Plan',         path: '/sampling-plan',         icon: TestTube,      desc: 'Predicted sample locations, expected values, per-visit input and trend over time', savedKey: 'rb_sampling_plan_v1' },
    ],
  },
  {
    number: 4,
    title: 'Reservoir Physics & Ecosystem',
    subtitle: 'Whole-reservoir behaviour: bed, thermal, algae, GHG',
    colour: 'from-emerald-700/40 to-emerald-900/20 border-emerald-700',
    accent: '#34d399',
    steps: [
      { label: 'Sediment Bed Physics',  path: '/sediment-bed-physics',  icon: Mountain,      desc: 'Bed-type physics across silt/sand for multiple depths' },
      { label: 'Bed Deep Assessment',   path: '/bed-assessment',        icon: FlaskConical,  desc: '15-section deep physics including advanced bubble corrections (v47)' },
      { label: 'Thermal Model',         path: '/thermal-model',         icon: Thermometer,   desc: 'Henderson-Sellers monthly heat budget, stratification, DO sat', savedKey: 'rb_thermal_model_v1' },
      { label: 'Algal Dynamics',        path: '/algal-dynamics',        icon: FlaskConical,  desc: 'Live aeration impact on algae + heterotrophic bacteria + nitrifiers, 180-day trajectory', savedKey: 'rb_algal_dynamics_v2' },
      { label: 'WQ Predictor (live)',   path: '/water-quality-predictor', icon: Activity,    desc: '11-variable ecosystem trajectory with adjustable aeration: WQI, DO, BOD, NH4, NO3, P, algae, bacteria', savedKey: 'rb_wq_predictor_v1' },
      { label: 'GHG Balance',           path: '/ghg-balance',           icon: Leaf,          desc: '20-year tCO₂e balance: methane, blower power, avoided treatment', savedKey: 'rb_ghg_balance_v1' },
    ],
  },
  {
    number: 5,
    title: 'Validation & Sensitivity',
    subtitle: 'Cross-check every calculation against literature bands',
    colour: 'from-lime-700/40 to-lime-900/20 border-lime-700',
    accent: '#a3e635',
    steps: [
      { label: 'Calculation Chain',     path: '/calculation-chain',     icon: Workflow,      desc: 'Single pore → diffuser → module → array → reservoir → algae, every step cited' },
      { label: 'Validation Steps',      path: '/validation-steps',      icon: ClipboardCheck, desc: 'Step-by-step PASS/WARN/FAIL across the design chain with live values' },
      { label: 'Calculation Steps',     path: '/reservoir-steps',       icon: ClipboardList, desc: 'Detailed step-by-step calculation report' },
      { label: 'Reservoir Validation',  path: '/reservoir-validation',  icon: ClipboardCheck, desc: 'Validation against literature bands across every regime' },
      { label: 'Diffuser Validation',   path: '/diffuser-validation',   icon: ClipboardCheck, desc: 'Validation of chosen diffuser against literature' },
      { label: 'Sensitivity (Tornado)', path: '/sensitivity',           icon: BarChart3,     desc: 'Saltelli OAT analysis: which inputs the module count is most sensitive to' },
    ],
  },
  {
    number: 6,
    title: 'Engineering & Plant',
    subtitle: 'Blowers, acoustics, fouling, plant equipment',
    colour: 'from-amber-700/40 to-amber-900/20 border-amber-700',
    accent: '#f59e0b',
    steps: [
      { label: 'Blower Selection',      path: '/blower-selection',      icon: Wind,          desc: 'Match seasonal demand against blower families with N+1 redundancy' },
      { label: 'Acoustic Noise',        path: '/acoustic-noise',        icon: Volume2,       desc: 'BS 4142:2014 specific LAeq at noise-sensitive receptors', savedKey: 'rb_acoustic_noise_v1' },
    ],
  },
  {
    number: 7,
    title: 'Compliance, Risk & Pilot',
    subtitle: 'Regulatory gateway, risk register, pilot trial',
    colour: 'from-orange-700/40 to-orange-900/20 border-orange-700',
    accent: '#fb923c',
    steps: [
      { label: 'Risk Register',         path: '/risk-register',         icon: ShieldAlert,   desc: 'Engineering, safety, environmental, commercial, regulatory risks', savedKey: 'rb_risk_register_v1' },
      { label: 'Reservoir Compliance',  path: '/reservoir-compliance',  icon: ShieldCheck,   desc: 'Reservoirs Act, DWI, EA, CDM 2015, DSEAR, BS 4142, SEW sign-off', savedKey: 'rb_reservoir_compliance_v1' },
      { label: 'Pilot Trial Planner',   path: '/pilot-trial-plan',      icon: FlaskConical,  desc: '8-week side-by-side trial schedule, instrumentation, success criteria', savedKey: 'rb_pilot_trial_v1' },
    ],
  },
  {
    number: 8,
    title: 'O&M & Lifecycle',
    subtitle: 'Operations, maintenance, long-term performance',
    colour: 'from-red-700/40 to-red-900/20 border-red-700',
    accent: '#f87171',
    steps: [
      { label: 'O&M Schedule',          path: '/om-schedule',           icon: Wrench,        desc: 'Year-by-year operations & maintenance calendar with spares', savedKey: 'rb_om_schedule_v1' },
    ],
  },
  {
    number: 9,
    title: 'Reports & Reference',
    subtitle: 'Final deliverables, citations, master report',
    colour: 'from-violet-700/40 to-violet-900/20 border-violet-700',
    accent: '#a78bfa',
    steps: [
      { label: 'MASTER REPORT',         path: '/reservoir-master',      icon: FileText,      desc: 'Consolidated 10-part design dossier — the headline deliverable' },
      { label: 'PDF Index',             path: '/pdf-index',             icon: FileText,      desc: 'Catalogue of every PDF the tool can generate' },
      { label: 'Citation Library',      path: '/citations',             icon: BookOpen,      desc: '60+ literature references with Harvard-style export' },
    ],
  },
];

interface SavedSnapshot {
  key: string;
  ts: string | null;
  preview: string;
}

function snapshotKey(key: string): SavedSnapshot {
  const ts = getPersistedTs(key);
  let preview = '';
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const obj = JSON.parse(raw);
      if (typeof obj === 'object' && obj !== null) {
        const entries = Object.entries(obj).slice(0, 4);
        preview = entries.map(([k, v]) => {
          if (typeof v === 'object') return `${k}: {…}`;
          const sv = String(v).slice(0, 30);
          return `${k}: ${sv}`;
        }).join(' · ');
      } else {
        preview = String(obj).slice(0, 60);
      }
    }
  } catch { /* ignore */ }
  return { key, ts, preview };
}

// Friendly labels for storage keys
const KEY_LABELS: Record<string, string> = {
  rb_diffuser_module_v1:      'Diffuser Module',
  rb_custom_diffuser_v1:      'Custom Diffuser',
  rb_custom_diffuser_export_v1: 'Custom Diffuser Export',
  rb_module_array_v1:         'Module Array',
  rb_bed_recommendations_v1:  'Bed Recommendations',
  rb_reservoir_design_v1:     'Reservoir Design',
  rb_reservoir_aeration_v1:   'Reservoir Aeration Spec',
  rb_site_data_capture_v1:    'Site Visit Form',
  rb_bathymetry_v1:           'Bathymetry Survey',
  rb_thermal_model_v1:        'Thermal Model',
  rb_algal_dynamics_v2:       'Algal Dynamics',
  rb_ghg_balance_v1:          'GHG Balance',
  rb_acoustic_noise_v1:       'Acoustic Noise',
  rb_fouling_drift_v1:        'Fouling Drift',
  rb_risk_register_v1:        'Risk Register',
  rb_reservoir_compliance_v1: 'Compliance Gateway',
  rb_pilot_trial_v1:          'Pilot Trial Planner',
  rb_om_schedule_v1:          'O&M Schedule',
  rb_sampling_plan_v1:        'Sampling Plan',
  rb_wq_predictor_v1:         'Water Quality Predictor',
  rb_simulation_data_v1:      'Simulation Data Hub',
};

export default function ReservoirHubPage() {
  const navigate = useNavigate();
  const [showSession, setShowSession] = useState(false);
  const [nonce, setNonce] = useState(0);
  const { enabledList, enabledCount } = useModulesEnabled();
  const totalAvailableModules = MODULES.filter(m => m.status === 'available').length;

  // Listen for storage changes so green dots refresh
  useEffect(() => {
    const handler = () => setNonce(n => n + 1);
    window.addEventListener('rb-persist-changed', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('rb-persist-changed', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const savedKeys = useMemo(() => {
    void nonce;
    const all = listAllPersistedKeys();
    return new Set(all);
  }, [nonce]);

  const snapshots = useMemo<SavedSnapshot[]>(() => {
    void nonce;
    return listAllPersistedKeys().map(snapshotKey).sort((a, b) =>
      (b.ts ?? '').localeCompare(a.ts ?? '')
    );
  }, [nonce]);

  const totalSaved = snapshots.length;
  const totalReservoir = snapshots.filter(s => s.key in KEY_LABELS).length;

  const exportSession = () => {
    const data = exportAllPersisted();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `reservoir_session_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    toast.success('Session exported');
  };

  return (
    <div className="space-y-6 p-4">
      <PageHeader
        icon={Waves}
        title="Reservoir Project Hub"
        subtitle="Single workflow view — every step in order, every saved session at a glance"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <AwwaM37Toggle />
            <button onClick={() => setShowSession(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-700 bg-emerald-900/20 px-3 py-1.5 text-sm font-semibold text-emerald-200 hover:bg-emerald-900/40">
              <Database className="w-4 h-4" />
              Session ({totalReservoir} saved)
              <span className="ml-1 inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            </button>
            <button onClick={exportSession}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <FileDown className="w-4 h-4" /> Export JSON
            </button>
          </div>
        }
      />

      {/* Hero status card */}
      <div className="rounded-2xl border-2 border-rb-brand/40 bg-gradient-to-br from-rb-brand/15 via-slate-900 to-slate-900 p-5">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-xl bg-rb-brand/20 border border-rb-brand/30 flex items-center justify-center shrink-0">
            <Waves className="w-7 h-7 text-rb-brand" />
          </div>
          <div className="flex-1 min-w-[280px]">
            <div className="text-lg font-bold text-slate-100">Barcombe Reservoir — Atkins Réalis / South East Water</div>
            <div className="text-sm text-slate-300 mt-1">
              Active reservoir-aeration design session. Every change made on any of the {PHASES.reduce((a, p) => a + p.steps.length, 0)} workflow pages below is automatically saved to the local session store and persists across reloads, browser restarts and tabs.
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-lg border border-emerald-700 bg-emerald-900/20 px-3 py-2 min-w-[88px]">
              <div className="text-2xl font-bold text-emerald-300">{totalReservoir}</div>
              <div className="text-[10px] text-emerald-300 uppercase tracking-wider">Saved pages</div>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 min-w-[88px]">
              <div className="text-2xl font-bold text-slate-100">{PHASES.reduce((a, p) => a + p.steps.length, 0)}</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider">Total steps</div>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 min-w-[88px]">
              <div className="text-2xl font-bold text-rb-brand">{PHASES.length}</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider">Phases</div>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced modules block */}
      <section className="rounded-2xl border-2 border-violet-700/50 bg-gradient-to-br from-violet-900/15 to-slate-900 p-4">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="text-sm font-bold text-violet-200">Advanced Modules — Tick to Enable</div>
            <div className="text-xs text-slate-400 mt-0.5">
              {enabledCount} of {totalAvailableModules} available modules enabled.
              Each module is an optional placement/optimisation enhancement that activates across the relevant pages.
            </div>
          </div>
          <button onClick={() => navigate('/modules')}
            className="inline-flex items-center gap-2 rounded-lg border border-violet-700 bg-violet-900/30 px-3 py-1.5 text-sm font-semibold text-violet-200 hover:bg-violet-900/50">
            <Boxes className="w-4 h-4" /> Open Modules Hub
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {MODULES.map(m => {
            const isOn = enabledList.some(e => e.id === m.id);
            const comingSoon = m.status === 'coming_soon';
            return (
              <button
                key={m.id}
                onClick={() => m.path && !comingSoon && navigate(m.path)}
                disabled={comingSoon}
                className={`text-left rounded-lg border p-2.5 transition ${
                  comingSoon ? 'border-slate-700 bg-slate-900/40 opacity-60 cursor-not-allowed' :
                  isOn ? 'border-emerald-600 bg-emerald-900/15 hover:bg-emerald-900/25' :
                  'border-slate-700 bg-slate-900 hover:border-rb-brand/50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                    comingSoon ? 'bg-amber-400' : isOn ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'
                  }`} />
                  <div className="text-xs font-semibold text-slate-100 truncate flex-1">{m.label}</div>
                  {comingSoon && (
                    <span className="text-[9px] text-amber-300 font-bold uppercase tracking-wider shrink-0">soon</span>
                  )}
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">{m.shortDesc}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Phases */}
      <div className="space-y-5">
        {PHASES.map(phase => (
          <section key={phase.number}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${phase.colour} border-2 flex items-center justify-center font-bold text-lg text-slate-100`}>
                {phase.number}
              </div>
              <div>
                <div className="text-lg font-bold text-slate-100">{phase.title}</div>
                <div className="text-xs text-slate-400">{phase.subtitle}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {phase.steps.map((step, si) => {
                const saved = step.savedKey ? savedKeys.has(step.savedKey) : false;
                return (
                  <FeatureCard
                    key={si}
                    icon={step.icon}
                    title={step.label}
                    desc={step.desc}
                    color={saved ? '#34d399' : phase.accent}
                    onClick={() => navigate(step.path)}
                    badge={saved ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                        <CheckCircle2 className="w-3 h-3" /> Saved
                      </span>
                    ) : undefined}
                  />
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <strong className="text-slate-200">Auto-save:</strong> every input on every workflow page above is
        persisted to <code className="bg-slate-800 px-1 rounded">localStorage</code> the moment it is
        changed. A green dot here indicates that page has saved data in the current session. The
        "Session" button at the top opens a popup listing every saved key with timestamp and content
        preview, and the "Export JSON" button serialises the full session for backup.
      </div>

      {/* Session summary modal */}
      {showSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
             onClick={() => setShowSession(false)}>
          <div className="rounded-2xl border border-slate-700 bg-slate-900 max-w-3xl w-full max-h-[85vh] flex flex-col"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-2">
                <Database className="w-5 h-5 text-rb-brand" />
                <h2 className="text-lg font-bold text-slate-100">Saved Session — {totalSaved} entries</h2>
                <span className="ml-1 inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              </div>
              <button onClick={() => setShowSession(false)}
                className="text-slate-400 hover:text-slate-200">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {snapshots.length === 0 && (
                <div className="text-center py-8 text-slate-500 text-sm">
                  No saved data yet. Open any workflow page to start a session.
                </div>
              )}
              {snapshots.map((s, i) => {
                const isReservoirKey = s.key in KEY_LABELS;
                return (
                  <div key={i} className={`rounded-lg border p-3 ${
                    isReservoirKey ? 'border-emerald-700/40 bg-emerald-900/10' : 'border-slate-700 bg-slate-800/40'
                  }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                          <div className="text-sm font-bold text-slate-100">
                            {KEY_LABELS[s.key] ?? s.key}
                          </div>
                          {!isReservoirKey && (
                            <span className="text-[10px] text-slate-500 font-mono">{s.key}</span>
                          )}
                        </div>
                        {s.preview && (
                          <div className="text-[11px] text-slate-400 mt-1 font-mono truncate">{s.preview}</div>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500 text-right shrink-0">
                        {s.ts ? new Date(s.ts).toLocaleString('en-GB') : '—'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-4 border-t border-slate-700 flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs text-slate-500">
                All data is stored in your browser's local storage and survives reloads, tabs and restarts.
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setNonce(n => n + 1)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-700">
                  <RefreshCcw className="w-3.5 h-3.5" /> Refresh
                </button>
                <button onClick={exportSession}
                  className="inline-flex items-center gap-1.5 rounded-md bg-rb-brand px-2.5 py-1 text-xs font-semibold text-white hover:bg-rb-brand/90">
                  <FileDown className="w-3.5 h-3.5" /> Export JSON
                </button>
                <button onClick={() => setShowSession(false)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
